import {Box, Occupancy} from "@variocube/app-api";
import {
    Avatar,
    Badge,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    usePlatform
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback} from "react";
import {logger} from "../logger";
import {CustomerDisplay} from "./customer";
import {OrderDisplay} from "./order";
import {useLocalization} from "../../i18n";

const log = logger.sub("BoxTable");

export interface BoxTableItem {
    box: Box;
    occupancy?: Occupancy;
    occupied: boolean;
}

export interface BoxTableProps {
    items: BoxTableItem[];
    allowOpenNonOccupied: boolean;
}

export function BoxTable({items, allowOpenNonOccupied}: BoxTableProps) {
    const {t} = useLocalization();
    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>{t("Box")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("Order")}</TableCell>
                    <TableCell>{t("Customer")}</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map(item => <BoxRow key={item.box.number} item={item} allowOpenNonOccupied={allowOpenNonOccupied}/>)}
            </TableBody>
        </Table>
    );
}

function BoxRow({item, allowOpenNonOccupied}: { item: BoxTableItem; allowOpenNonOccupied: boolean; }) {
    const {box, occupancy, occupied} = item;

    const platform = usePlatform();
    //const strings = useStrings(resources);
    const {t} = useLocalization();

    const canOpen = Boolean(occupancy || (allowOpenNonOccupied && !occupied));

    const handleOpen = useCallback(async () => {
        if (canOpen) {
            log.info("Opening box", box);
            try {
                await platform.openLock(box.lock);
            }
            catch (error) {
                log.error("Failed to open lock", box.lock);
            }
        }
    }, [platform, occupancy, occupied, box]);

    return (
        <TableRow>
            <TableCell>
                <Avatar size={1.5}>{box.number}</Avatar>
            </TableCell>
            <TableCell>
                {occupied && <Badge>{t("Occupied")}</Badge>}
                {box.maintenanceRequiredAt && <Badge>{t("MaintananceRequired")}</Badge>}
            </TableCell>
            <TableCell>
                {occupancy && (
                    <OrderDisplay order={occupancy.content.order}/>
                )}
            </TableCell>
            <TableCell>
                {occupancy && (
                    <CustomerDisplay customer={occupancy.content.order.customer}/>
                )}
            </TableCell>
            <TableCell>
                {canOpen && (
                    <PushButton onClick={handleOpen}>
                        {t("OpenBox")}
                    </PushButton>
                )}
            </TableCell>
        </TableRow>
    );
}
